import { jsPDF } from "jspdf";
import { MissionEnums, MissionEnumsLists } from "./constants";
import moment from "moment/moment";
import { Task } from "@emberly/zenith-client";
import { GetDaysStored, GetLightboxSlide, GetStoragePrice } from "./mission";
import { FormatLocation } from "./maphelpers";

const TEXT_SECONDARY = "#555555";
const DATETIME_FORMAT = "DD.MM.YYYY HH:mm";
const DATE_FORMAT = "DD.MM.YYYY";

export async function MakeMissionPDF(settings, mission, station, exportSettings, t) {
  
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
    orientation: "portrait"
  });

  let html = await RenderToHtml(settings, mission, station, exportSettings, t);
  let render = await RenderFromHTML(html, doc);

  if (settings.images) {
    await addImages(render, mission);
  }

  await addHeaders(render, mission, station, t);
  await addFooters(render, mission, station, t);

  return render;
}


async function RenderFromHTML(html, doc) {
  const task = new Task();
  const printableWidth = doc.internal.pageSize.getWidth();
  
  doc.html(html, {
    autoPaging: "text",
    margin: [56, 32, 56, 32],
    width: printableWidth,
    windowWidth: 1000,
    html2canvas: { scale: 0.5 },
    callback: (doc) => {
      task.complete(doc);
    }
  });

  return task.wait();
}

async function RenderToHtml(settings, mission, station, exportSettings, t) {
  return (`
    <!DOCTYPE html>
    <html>
      <body>
        ${await renderMissionHTML(settings, mission, station, exportSettings, t)}
      </body>
    </html>
  `);
}


async function renderMissionHTML(settings, mission, station, exportSettings, t) {

  // Start building
  let html = "";

  html += `<div style="font-family: sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 4px;">${station.legalName || ""}</div>`;

  if (!!exportSettings.organizationNumber) {
    html += `<div style="font-family: sans-serif; font-size: 20px; margin-bottom: 4px;">${t("common:organizationNumber")}: ${exportSettings.organizationNumber || "-"}</div>`;
  }

  if (!!exportSettings.organizationLocation) {
    html += `<div style="font-family: sans-serif; font-size: 20px; margin-bottom: 4px;">${FormatLocation(exportSettings.organizationLocation)}</div>`;
  }

  html += `<h1 style="font-family: sans-serif; font-size: 52px; padding-top: 20px;">${t("mission:mission")} ${mission.number}</h1>`;
  html += `<h4 style="font-family: sans-serif; font-weight: normal; font-size: 20px; letter-spacing: 0.05em; margin-left: 4px; margin-bottom: 16px;">${t("common:tables:state")}: ${t(`mission:enums:state:${MissionEnumsLists.State[mission.state] || 0}`)}, ${t("common:created")}: ${moment(mission.created).format(DATETIME_FORMAT)}</h4>`;
  html += `<table border="0" cellpadding="4" cellspacing="0" style="border-collapse:collapse; width:100%; border: none;">`;

  const { details, target, actors, serviceInfo } = mission;
  const { hasContactPerson, ownerIsNotRequester, contract } = actors;

  // Info
  html += th(t("mission:cards:details:service"));
  html += tr(t("mission:cards:details:type"), t(`mission:enums:type:${MissionEnumsLists.Type[details.type]}`));

  if (!!details?.cause?.name) {
    html += tr(t("mission:cards:details:cause"), details.cause?.name);
  }

  html += tr(t("mission:cards:details:description"), details.description || "-");
  html += tr(t("common:tables:tags"), details?.tags?.map(t => t.name).join(', ') || "-");


  // TARGET
  html += th(t("mission:cards:target:object"));
  html += tr(t("mission:cards:target:type"), t(`mission:enums:targetType:${MissionEnumsLists.TargetType[target.type || 0]}`));

  if (target.type === MissionEnums.TargetType.Car) {
    const carDetails = target.carDetails;
    html += tr(t("mission:cards:target:registration"), carDetails.registration);
    html += tr(t("mission:cards:target:chassisNumber"), carDetails.chassisNumber);
    html += tr(t("mission:cards:target:make"), carDetails.make);
    html += tr(t("mission:cards:target:model"), carDetails.model);
    html += tr(t("mission:cards:target:color"), carDetails.color);
    html += tr(t("mission:cards:target:driveTrain"), t(`mission:enums:driveTrain:${MissionEnumsLists.DriveTrain[carDetails.driveTrain]}`));
    html += tr(t("mission:cards:target:fuel"), t(`mission:enums:fuelType:${MissionEnumsLists.FuelType[carDetails.fuel]}`));
    html += tr(t("mission:cards:target:weight"), !!carDetails.weightInKG ? `${carDetails.weightInKG}kg` : "-");
    html += tr(t("mission:cards:target:year"), carDetails.year);

  } else if (target.type === MissionEnums.TargetType.Boat) {
    const boatDetails = target.boatDetails;
    html += tr(t("mission:cards:target:registration"), boatDetails.registration);
    html += tr(t("mission:cards:target:make"), boatDetails.make);
    html += tr(t("mission:cards:target:model"), boatDetails.model);
    html += tr(t("mission:cards:target:year"), boatDetails.year);
    html += tr(t("mission:cards:target:length"), boatDetails.lengthInFeet);
    html += tr(t("mission:cards:target:weight"), boatDetails.weightInKG);
  }

  html += tr(t("mission:cards:target:description"), target.description || "-");

  // SERVICE
  if (settings.serviceInfo && !!serviceInfo) {
    html += th(t("mission:cards:service:title"));
    html += tr(t("mission:cards:service:lastService"), !!serviceInfo.lastService ? moment(serviceInfo.lastService).format(DATE_FORMAT) : "-");
    html += tr(t("mission:cards:service:serviceCenter"), serviceInfo.serviceCenter || "-");
    html += tr(t("mission:cards:service:validTo"), !!serviceInfo.validTo ? moment(serviceInfo.validTo).format(DATE_FORMAT) : "-");
    html += tr(t("mission:cards:service:mileageAtService"), serviceInfo.mileageAtService || "-");
    html += tr(t("mission:cards:service:mileage"), serviceInfo.currentMileage || "-");
    html += tr(t("mission:cards:service:validWarranty"), serviceInfo.validWarranty ? t("common:yes") : t("common:no"));
  }

  // ACTORS
  if (settings.actors) {

    html += addContact(
      ownerIsNotRequester ? t("mission:cards:actors:actors:owner") : `${t("mission:cards:actors:actors:owner")}/${t("mission:cards:actors:actors:requester")}`,
      actors.owner,
      t
    );

    if (ownerIsNotRequester) {
      html += addContact(
        t("mission:cards:actors:actors:requester"),
        actors.requester,
        t
      );
    }

    if (hasContactPerson) {
      html += addContact(
        t("mission:cards:actors:actors:contactPerson"),
        actors.contactPerson,
        t
      );
    }
  }

  // CONTRACT
  if (settings.contract) {
    html += th(t("mission:cards:actors:contract:title"));
    html += tr(t("mission:cards:actors:contract:agreement"), contract?.agreement?.name || "-");

    if (!!contract?.deductible?.value) {
      html += tr(t("mission:cards:actors:contract:deductible"), contract?.deductible?.value || "-");
    }

    html += tr(t("mission:cards:actors:contract:reference"), contract?.reference || "-");
    html += tr(t("mission:cards:actors:contract:comment"), contract?.description || "-");

    if (contract.isInsuranceContract) {
      const insurance = contract.insurance;
      html += tr(t("mission:cards:actors:contract:insuranceCompany"), insurance?.insurer || "-");
      html += tr(t("mission:cards:actors:contract:insuranceType"), t(`mission:enums:insuranceType:${MissionEnumsLists.InsuranceType[insurance.type || 0]}`));
      html += tr(t("mission:cards:actors:contract:insuranceState"), t(`mission:enums:insuranceState:${MissionEnumsLists.InsuranceState[insurance.state || 0]}`));
    }
  }


  // TASKS

  for (let i = 0; i < mission.salvageTasks.length; i++) {
    const task = mission.salvageTasks[i];

    if (!settings.tasks.includes(task.id)) continue;

    const { route, execution } = task;
    const { waypoints } = route;
    const { history } = execution;

    const distanceInRouteKM = sanitizeNumber(pickLastValid(route?.distanceInRouteKM, route?.overriddenDistanceInRouteKM));
    const distanceToRouteKM = sanitizeNumber(pickLastValid(route?.distanceToRouteKM, route?.overriddenDistanceToRouteKM));


    html += th(`${t("mission:pdf:task")}${mission.salvageTasks.length === 1 ? "" : " " + task.number}${!!task.taskName ? `: ${task.taskName}` : ""}`);

    html += tr(t("mission:cards:tasks:salvageTask:description"), task.description || "-");
    html += tr(t("mission:cards:tasks:salvageTask:customerWaiting"), t(`mission:enums:locationState:${MissionEnumsLists.LocationState[task.locationState || 0]}`));

    if (task.locationState === 1) {
      html += tr(t("mission:cards:tasks:salvageTask:keyPlacement"), task.keyPlacement || "-");
    }

    html += tr(t("mission:cards:tasks:salvageTask:comment"), task.comment || "-");
    html += tr(t("mission:cards:tasks:salvageTask:eta"), !!execution.estimatedArrival ? moment(execution.estimatedArrival).format(DATETIME_FORMAT) : "-");

    html += tr(t("mission:cards:tasks:driver:title"), execution?.driver?.name || "-");
    html += tr(t("mission:cards:tasks:driver:activeCar"), execution?.vehicle?.name || "-");

    html += tr(
      t("mission:cards:tasks:driver:states:title"),
      Object.keys(history)
        .filter(stateKey => stateKey !== "responded" && stateKey !== "assigned" && stateKey !== "created")
        .map(stateKey => t(`mission:cards:tasks:driver:states:${stateKey}`) + ": " + (!!history[stateKey]?.lastModified ? moment(history[stateKey]?.lastModified).format(DATETIME_FORMAT) : "-"))
        .join("<br/>")
    );

    html += tr(
      t("mission:cards:tasks:map:detailsCardTitle"),
      waypoints
        .filter(location => !(location.id === null || (location.coordinates === null && !location.address)))
        .map((location, idx) => t(idx === 0 ? "mission:cards:tasks:salvageTask:missionLocation" : idx === waypoints.length - 1 ? "mission:cards:tasks:salvageTask:transportTo" : "mission:cards:tasks:salvageTask:waypoint") + ": " + FormatLocation(location))
        .concat([
          `${t("mission:cards:tasks:map:overrideDialog:startLocation")}: ${route.overriddenStartLocation?.name || station.displayName}`,
          !!distanceInRouteKM ? `${t("mission:cards:tasks:map:distanceInRoute")}: ${distanceInRouteKM} km` : null,
          !!distanceToRouteKM ? `${t("mission:cards:tasks:map:distanceToRoute")}: ${distanceToRouteKM} km` : null,
        ].filter(t => !!t))
        .join("<br/>")
    );

  }


  // STORAGE
  if (settings.storage && !!mission.storageTask) {
    const { storageTask } = mission;

    html += th(t("mission:cards:tasks:storageTask:defaultName"));
    html += tr(t("mission:cards:tasks:storageTask:stored"), !!storageTask.stored ? moment(storageTask.stored).format(DATE_FORMAT) : "-");
    html += tr(t("mission:cards:tasks:storageTask:warehouse"), storageTask?.warehouse?.name || "-");

    html += tr(t("mission:cards:tasks:storageTask:comment"), storageTask?.comment || "-");
    html += tr(t("mission:cards:tasks:storageTask:keyNumber"), storageTask?.keyNumber || "-");

    const daysElapsed = GetDaysStored(storageTask);
    const accCosts = GetStoragePrice(storageTask);

    html += tr(t("mission:cards:tasks:storageTask:state:elapsedTime"), daysElapsed);
    html += tr(t("mission:cards:tasks:storageTask:state:accumulatedCosts"), accCosts);
    html += tr(t("mission:cards:tasks:storageTask:state:delivered"), storageTask.delivered ? moment(storageTask.deliveryTime).format(DATETIME_FORMAT) : "-");
  }


  // MISSION NOTES
  if (settings.missionNotes && !!mission.missionNotes) {
    const { missionNotes } = mission;
    const { notes } = missionNotes;
    html += th(t("mission:cards:missionNotes:title"));

    for (let i = 0; i < notes.length; i++) {
      const note = notes[i];

      html += trfull(
        `${note.sender?.name?.replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '') || ""} - ${moment(note.created).format(DATETIME_FORMAT)}`,
        note.content?.replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '') || "-"
      );
    }
  }


  html += `</table>`;

  return html;
}


function addContact(title, contact, t) {
  if (!contact) return "";

  let html = th(title);

  html += tr(t("common:name"), contact.name);

  if (!!contact.location) {
    html += tr(t("entity:location:address"), FormatLocation(contact.location) || "-");
  }

  if (!!contact.email) {
    html += tr(t("common:email"), contact.email || "-");
  }

  html += tr(t("common:phoneNumber"), contact.phoneNumber || "-");

  return html;
}

function tr(title, text) {
  return `<tr><td style="color: ${TEXT_SECONDARY}; font-family: sans-serif; font-weight: normal; font-size: 20px; padding-bottom: 8px; letter-spacing: 0.05em; vertical-align: top;">${title}</td><td style="font-family: sans-serif; font-weight: normal; font-size: 20px; padding-bottom: 8px; letter-spacing: 0.05em; vertical-align: top; line-height: 125%;">${text}</td></tr>`;
}

function th(title) {
  return `<tr><th colspan="2" style="font-family: sans-serif; text-align:left; padding-top: 32px; padding-bottom: 8px; font-size: 24px; letter-spacing: 0.05em;">${title}</th></tr>`;
}


function trfull(title, text) {
  return `<tr><td colspan="2" style="color: ${TEXT_SECONDARY}; font-family: sans-serif; font-weight: normal; font-size: 20px; padding-bottom: 8px; letter-spacing: 0.05em; vertical-align: top;">${title}<br/><span style="color: #000; font-family: sans-serif; font-weight: normal; font-size: 20px; padding-bottom: 8px; letter-spacing: 0.05em; vertical-align: top; line-height: 125%;">${text}</span></td></tr>`;
}

async function addImages(doc, mission) {

  const uploads = mission.files && mission.files.uploads ? Object.values(mission.files.uploads).filter(t => t.type === 1 && t.complete) : [];
  const padding = 32;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 0; i < uploads.length; i++) {
    doc.addPage();

    const upload = uploads[i];
    const lbox = GetLightboxSlide(upload);

    const { dataURL, width, height } = await getImageData(lbox.src);

    const usableWidth = pageWidth - 2 * padding;
    const usableHeight = pageHeight - 2 * padding;

    // Calculate aspect ratio scaling (fit to padded area)
    const widthRatio = usableWidth / width;
    const heightRatio = usableHeight / height;
    const scale = Math.min(widthRatio, heightRatio);

    const scaledW = width * scale;
    const scaledH = height * scale;

    const x = padding + (usableWidth - scaledW) / 2;
    const y = padding + (usableHeight - scaledH) / 2;

    doc.addImage(dataURL, "JPEG", x, y, scaledW, scaledH);
  }
}


function getImageData(url, radius = 0, format = "image/jpeg", origin = "anonymous") {
  return new Promise((resolve, reject) => {
    let img = new window.Image();

    if (origin !== false) {
      img.setAttribute("crossOrigin", origin);
    }

    img.onload = function () {
      let canvas = document.createElement("canvas");
      canvas.width = this.width;
      canvas.height = this.height;
      let ctx = canvas.getContext("2d");

      if (radius > 0) {
        ctx.save();
        roundRect(ctx, 0, 0, canvas.width, canvas.height, radius);
        ctx.clip();
      }

      ctx.drawImage(this, 0, 0);

      // Restore context if clipped
      if (radius > 0) ctx.restore();

      let dataURL = canvas.toDataURL(format);
      resolve({ dataURL, width: this.width, height: this.height });
    };

    img.onerror = reject;
    img.src = url;
  });
}


function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}


async function addHeaders(doc, mission, station, t) {
  const pageCount = doc.internal.getNumberOfPages();

  const hasLogo = !!station.logo && station.logo?.startsWith("https://");

  const { dataURL } = hasLogo ? await getImageData(station.logo, 32, "image/png") : { dataURL: "" };


  for (var i = 1; i <= pageCount; i++) {
    // Set the page
    doc.setPage(i);

    // Add logo
    if (hasLogo) {
      doc.addImage(dataURL, "PNG", 32, 8, 24, 24);
    }

    doc.setTextColor(TEXT_SECONDARY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    // Render text
    doc.text(
      `${station.displayName}`,
      hasLogo ? 64 : 32,
      24,
      {
        align: "left"
      }
    );

    doc.setTextColor(TEXT_SECONDARY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    doc.text(
      `${t("mission:mission")} ${mission.number}`,
      doc.internal.pageSize.width / 2,
      24,
      {
        align: "center"
      }
    );

    doc.text(
      `${t("mission:print")}: ${moment().format(DATETIME_FORMAT)}`,
      doc.internal.pageSize.width - 32,
      24,
      {
        align: "right"
      }
    );
  }


  doc.setTextColor("#000000");
}



async function addFooters(doc, mission, station, t) {
  const pageCount = doc.internal.getNumberOfPages();

  const { dataURL, width, height } = await getImageData("/assets/icons/logo.svg", 0, "image/png");

  doc.setTextColor(TEXT_SECONDARY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  for (var i = 1; i <= pageCount; i++) {
    // Set the page
    doc.setPage(i);

    // Add logo
    doc.addImage(dataURL, "PNG", (doc.internal.pageSize.width - 50) / 2, doc.internal.pageSize.height - 24, 50, 12);

    // Render text
    doc.text(
      t("mission:pdf:doneInAssist"),
      32,
      doc.internal.pageSize.height - 12,
      {
        align: "left"
      }
    );

    doc.text(
      `${t("mission:pdf:page")} ${i} ${t("mission:pdf:of")} ${pageCount}`,
      doc.internal.pageSize.width - 32,
      doc.internal.pageSize.height - 12,
      {
        align: "right"
      }
    );
  }

  doc.setTextColor("#000000");
}


const sanitizeNumber = (n) => isNaN(n) || n === -1 ? 0 : n;
const pickLastValid = (a, b) => typeof b === "number" ? b : a;
